import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import { Input } from "@frui.ts/bootstrap";
import { observer } from "mobx-react-lite";
import React from "react";
import { Alert, Col, Form } from "react-bootstrap";
import type OutageDetailViewModel from "viewModels/administration/outages/outageDetailViewModel";
import type OutageCreateViewModel from "viewModels/administration/outages/outageCreateViewModel";
import FormGroup from "controls/formGroup";
import FormLabel from "controls/formLabel";
import OutageState from "entities/outageState";
import { getOutageVariant } from "viewModels/administration/outages/utils";
import SystemComponentCode from "../../../entities/systemComponentCode";
import LanguageCode from "../../../entities/languageCode";
import DatePickerBox from "controls/datePickerBox";
import TimeInput from "controls/timeInput";

export interface OutageFormProps {
  vm: OutageCreateViewModel | OutageDetailViewModel;
  children?: React.ReactNode;
}

const OutageForm = observer((props: OutageFormProps) => {
  const { vm, children } = props;
  const disabled = !vm.isEditable;

  // Show description when component is not selected yet or it's not of type Other
  const isOtherOutage = vm.item?.systemComponentId !== undefined && vm.systemComponentCode === SystemComponentCode.OTHER;

  const customOutageCs = vm.item?.systemComponentOutageLocalizations?.find(outage => outage.languageCode === LanguageCode.Cs);
  const customOutageEn = vm.item?.systemComponentOutageLocalizations?.find(outage => outage.languageCode === LanguageCode.En);

  return (
    <>
      <Form.Row>
        <FormGroup as={Col} md="3" target={vm.item} property="visibleSince">
          <FormLabel>{vm.translateAttribute("visibleSince")}</FormLabel>
          <DatePickerBox
            placementOverlay="top-start"
            strategyOverlay="fixed"
            placeholder={vm.translateAttribute("visibleSince")}
            disabled={disabled}
          />
        </FormGroup>
        <FormGroup as={Col} md="3" target={vm.item} property="_visibleSinceTime">
          <FormLabel>{vm.translate("time")}</FormLabel>
          <TimeInput className="mw-100" disabled={disabled} />
        </FormGroup>
        <FormGroup as={Col} md="6" target={vm.item} property="systemComponentId">
          <FormLabel>{vm.translateAttribute("systemComponentId")}</FormLabel>
          <AdvancedSelect items={vm.systemComponentOptions} mode="key" disabled={disabled} />
        </FormGroup>
      </Form.Row>
      <Form.Row>
        <FormGroup as={Col} md="3" target={vm.item} property="validSince">
          <FormLabel>{vm.translateAttribute("validSince")}</FormLabel>
          <DatePickerBox
            placementOverlay="top-start"
            strategyOverlay="fixed"
            placeholder={vm.translateAttribute("validSince")}
            disabled={disabled}
          />
        </FormGroup>
        <FormGroup as={Col} md="3" target={vm.item} property="_validSinceTime">
          <FormLabel>{vm.translate("time")}</FormLabel>
          <TimeInput className="mw-100" disabled={disabled} />
        </FormGroup>
        <FormGroup as={Col} md="3" target={vm.item} property="validUntil">
          <FormLabel>{vm.translateAttribute("validUntil")}</FormLabel>
          <DatePickerBox
            placementOverlay="top-start"
            strategyOverlay="fixed"
            placeholder={vm.translateAttribute("validUntil")}
            disabled={disabled}
          />
        </FormGroup>
        <FormGroup as={Col} md="3" target={vm.item} property="_validUntilTime">
          <FormLabel>{vm.translate("time")}</FormLabel>
          <TimeInput className="mw-100" disabled={disabled} />
        </FormGroup>
      </Form.Row>
      {!isOtherOutage && (
        <Form.Row>
          <FormGroup as={Col} target={vm.item} property="description">
            <FormLabel>{vm.translateAttribute("description")}</FormLabel>
            <Input as="textarea" rows={3} disabled={disabled} />
          </FormGroup>
        </Form.Row>
      )}
      {isOtherOutage && (
        <>
          <Form.Row>
            <FormGroup as={Col} target={vm.item} property="customOutageCs">
              <FormLabel required={true}>{vm.translate("customOutageLabelCs")}</FormLabel>
              <Input as="textarea" rows={3} disabled={disabled} />
            </FormGroup>
          </Form.Row>
          <Form.Row>
            <FormGroup as={Col} target={vm.item} property="customOutageEn">
              <FormLabel required={true}>{vm.translate("customOutageLabelEn")}</FormLabel>
              <Input as="textarea" rows={3} disabled={disabled} />
            </FormGroup>
          </Form.Row>
        </>
      )}

      {vm.item && (
        <div className="mt-6 mb-4" style={{ maxWidth: 1100 }}>
          <hr />
          <div className="align-items-center row">
            <div className="col-6 col-lg-8">
              <h4 className="mt-4">{vm.translate("outagePreview")}</h4>
            </div>
            <div className="col-6 col-lg-4">
              <AdvancedSelect
                target={vm}
                property="outageState"
                items={[
                  { id: OutageState.ACTIVE, label: vm.translate("active") },
                  { id: OutageState.VISIBLE, label: vm.translate("visible") },
                ]}
              />
            </div>
          </div>
          <p>{vm.impact}</p>
          {!isOtherOutage && vm.outageMessage && (
            <Alert variant={getOutageVariant(vm.item, vm.systemComponentCode, vm.outageState)}>
              {vm.outageMessage}
              {vm.item.description && <p className="mt-2">{vm.item.description}</p>}
            </Alert>
          )}
          {isOtherOutage && (
            <>
              {customOutageCs && customOutageCs.description.length > 0 && (
                <>
                  <h5>{vm.translate("customOutagePreviewCs")}</h5>
                  <Alert variant={getOutageVariant(vm.item, vm.systemComponentCode, vm.outageState)}>
                    {customOutageCs.description}
                  </Alert>
                </>
              )}
              {customOutageEn && customOutageEn.description.length > 0 && (
                <>
                  <h5>{vm.translate("customOutagePreviewEn")}</h5>
                  <Alert variant={getOutageVariant(vm.item, vm.systemComponentCode, vm.outageState)}>
                    {customOutageEn.description}
                  </Alert>
                </>
              )}
            </>
          )}
        </div>
      )}
      {children}
    </>
  );
});

export default OutageForm;
